/**
 * @fileoverview Modal para gerenciar targets de promoções.
 * @module TargetManager
 * @requires react
 * @requires @mui/material
 * @requires @mui/icons-material
 * @since 1.0.0
 */

import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Box,
  Chip,
} from '@mui/material';
import { Delete as DeleteIcon, Add as AddIcon } from '@mui/icons-material';
import TargetSelector from './TargetSelector';

/**
 * Modal para gerenciar lista de targets.
 * Armazena targets como objetos {id, name} internamente.
 * @component
 * @param {Object} props
 * @param {boolean} props.open - Se o modal está aberto
 * @param {Function} props.onClose - Callback para fechar modal
 * @param {Object} props.targets - Targets atuais (arrays de IDs ou objetos)
 * @param {Function} props.onSave - Callback ao salvar targets
 * @returns {JSX.Element}
 */
export default function TargetManager({ open, onClose, targets, onSave }) {
  const [localTargets, setLocalTargets] = useState(targets);
  const [selectorOpen, setSelectorOpen] = useState(false);

  /**
   * Converte targets em lista plana para exibição.
   * @function getTargetsList
   * @returns {Array} Lista de targets
   */
  const getTargetsList = () => {
    const list = [];
    
    if (localTargets.products) {
      localTargets.products.forEach(item => {
        const target = typeof item === 'object' ? item : { id: item, name: item };
        list.push({ ...target, type: 'products', typeName: 'Produto' });
      });
    }
    
    if (localTargets.categories) {
      localTargets.categories.forEach(item => {
        const target = typeof item === 'object' ? item : { id: item, name: item };
        list.push({ ...target, type: 'categories', typeName: 'Categoria' });
      });
    }
    
    if (localTargets.brands) {
      localTargets.brands.forEach(item => {
        const target = typeof item === 'object' ? item : { id: item, name: item };
        list.push({ ...target, type: 'brands', typeName: 'Marca' });
      });
    }
    
    return list;
  };

  /**
   * Remove um target da lista.
   * @function handleRemove
   * @param {string|number} id - ID do target
   * @param {string} type - Tipo do target
   * @returns {void}
   */
  const handleRemove = (id, type) => {
    const newTargets = { ...localTargets };
    newTargets[type] = newTargets[type].filter(item => 
      (typeof item === 'object' ? item.id : item) !== id
    );
    setLocalTargets(newTargets);
  };

  /**
   * Adiciona novos targets.
   * @function handleAddTargets
   * @param {Array} items - Itens selecionados
   * @returns {void}
   */
  const handleAddTargets = (items) => {
    const newTargets = { ...localTargets };
    
    items.forEach(item => {
      const exists = newTargets[item.type].some(t => 
        (typeof t === 'object' ? t.id : t) === item.id
      );
      
      if (!exists) {
        newTargets[item.type].push({ id: item.id, name: item.name });
      }
    });
    
    setLocalTargets(newTargets);
    setSelectorOpen(false);
  };

  /**
   * Salva targets.
   * @function handleSave
   * @returns {void}
   */
  const handleSave = () => {
    onSave(localTargets);
  };

  const targetsList = getTargetsList();

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>Gerenciar Itens da Promoção</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setSelectorOpen(true)}
            >
              Adicionar Itens
            </Button>
          </Box>

          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Tipo</TableCell>
                  <TableCell>Nome</TableCell>
                  <TableCell>ID</TableCell>
                  <TableCell align="right">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {targetsList.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      Nenhum item adicionado
                    </TableCell>
                  </TableRow>
                ) : (
                  targetsList.map((target, index) => (
                    <TableRow key={`${target.type}-${target.id}-${index}`}>
                      <TableCell>
                        <Chip 
                          label={target.typeName} 
                          size="small" 
                          color={
                            target.type === 'products' ? 'primary' :
                            target.type === 'categories' ? 'secondary' : 'default'
                          }
                        />
                      </TableCell>
                      <TableCell>{target.name || target.id}</TableCell>
                      <TableCell>{target.id}</TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={() => handleRemove(target.id, target.type)}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancelar</Button>
          <Button onClick={handleSave} variant="contained">
            Salvar
          </Button>
        </DialogActions>
      </Dialog>

      <TargetSelector
        open={selectorOpen}
        onClose={() => setSelectorOpen(false)}
        onAdd={handleAddTargets}
      />
    </>
  );
}
